import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { useAtomValue, useSetAtom } from 'jotai';
import uniqueId from 'lodash/uniqueId';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useProgressLoading } from 'modules/utils';
import moment from 'moment';
import { FC, memo, useMemo } from 'react';
import { useLifecycles } from 'react-use';
import {
    useNetSuiteAttachmentsFieldSettings,
    useNetSuiteBillPaymentFieldSettings,
    useUserBetaFeatures,
} from 'shared/hooks';
import { netSuiteBillPaymentApplyListState, netSuiteBillPaymentFieldValuesState } from 'shared/states';

import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import { getNetSuiteContext } from '../../../selectors/netsuite/netSuiteSelectors';
import { getContext } from '../../../selectors/pageSelectors';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { convertCustomFieldsToState, mapAddress, setCurrencyPrecision } from '../helpers';
import { useCurrencyPrecision, useCustomFields, useResetBillPaymentFormState, useUploadAttachments } from '../hooks';
import { useExchangeRates } from '../hooks/useExchangeRates/useExchangeRates';
import { isFieldMandatory, isFieldVisible } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { Content, ContentInner, Root, Toolbar, ToolbarText } from '../NetSuiteRequestForm/NetSuiteRequestForm.styles';
import { NetSuiteBillPaymentBillsSection } from './components/NetSuiteBillPaymentBillsSection/NetSuiteBillPaymentBillsSection';
import { NetSuiteBillPaymentFieldsSection } from './components/NetSuiteBillPaymentFieldsSection/NetSuiteBillPaymentFieldsSection';
import {
    useGetRequestTransfer,
    useGetValidationErrors,
    useNetSuiteBillPaymentFields,
    useSaveAndSubmitMutation,
} from './NetSuiteBillPaymentRequestForm.hooks';
import { messages } from './NetSuiteBillPaymentRequestForm.messages';
import { netSuiteBillPaymentCreditListState } from './NetSuiteBillPaymentRequestForm.states';
import { NetSuiteBillPaymentRequestFormProps } from './NetSuiteBillPaymentRequestForm.types';

export const NetSuiteBillPaymentRequestForm: FC<NetSuiteBillPaymentRequestFormProps> = memo((props) => {
    const { request } = props;

    const pageContext = useSelector(getContext);
    const editingContext = useSelector(getNetSuiteContext);
    const fieldValues = useAtomValue(netSuiteBillPaymentFieldValuesState);
    const setApplyList = useSetAtom(netSuiteBillPaymentApplyListState);
    const setCreditList = useSetAtom(netSuiteBillPaymentCreditListState);

    const { updateFieldsBulk } = useNetSuiteBillPaymentFields();

    const { currencies, customFields, documentFields } = editingContext;

    const fieldSettings = useNetSuiteBillPaymentFieldSettings(documentFields);

    const { setCustomFieldValuesBulk } = useCustomFields(customFields);

    const { onUploadAttachments } = useUploadAttachments(request.id);

    const currencyPrecision = useCurrencyPrecision(fieldValues.currency ?? request.details.currency);

    const attachmentsFieldSettings = useNetSuiteAttachmentsFieldSettings(documentFields);

    const resetBillPaymentFormState = useResetBillPaymentFormState();

    const { data: dataExchangeRates } = useExchangeRates(request.companyId, fieldValues.transactionDate);

    const defaultCurrencyRate = useMemo(() => {
        const exchangeRate = dataExchangeRates?.rates.find((currency) => {
            return currency.id === fieldValues.currency?.id;
        });

        return exchangeRate?.rateValue;
    }, [dataExchangeRates, fieldValues.currency]);

    const currency = useMemo(() => {
        return currencies.find((currency) => currency.id === fieldValues.currency?.id) ?? null;
    }, [currencies, fieldValues.currency]);

    const getRequestTransfer = useGetRequestTransfer(
        request,
        currency,
        pageContext.templateVersion,
        defaultCurrencyRate
    );

    const getValidationErrors = useGetValidationErrors({
        customFields,
        fieldSettings,
        defaultCurrencyRate,
        requestCurrencyISOCode: currency?.ISOCode ?? null,
        workflowId: request.template.id,
        companyCurrency: request.companyCurrency,
        attachmentsFieldSettings,
        attachments: request.attachments,
    });

    const { saveAndSubmit, isSaving } = useSaveAndSubmitMutation(request, getValidationErrors, getRequestTransfer);

    useLifecycles(
        () => {
            const { customFields, applyList, creditList, ...fields } = request.details;

            const transactionDate = fields.transactionDate || moment().toISOString();

            updateFieldsBulk(
                {
                    ...fields,
                    payee: fields.payee
                        ? {
                              ...fields.payee,
                              type: fields.payeeType,
                          }
                        : null,
                    payeeAddress: mapAddress(fields.payeeAddress),
                    transactionDate,
                    exchangeRate:
                        request.exchangeRateSource === domain.ExchangeRateSource.Manual ? fields.exchangeRate : null,
                },
                false
            );

            if (applyList.length) {
                setApplyList(
                    applyList.map((applyListItem) => ({
                        ...applyListItem,
                        amountDue: setCurrencyPrecision(applyListItem.amountDue, currencyPrecision),
                        discountAmount: setCurrencyPrecision(applyListItem.discountAmount, currencyPrecision),
                        paymentAmount: setCurrencyPrecision(applyListItem.paymentAmount, currencyPrecision),
                        isEmpty: false,
                        id: uniqueId('netSuiteApplyListLine-'),
                    }))
                );
            }

            if (creditList.length) {
                setCreditList(
                    creditList.map((creditListLine) => ({
                        ...creditListLine,
                        paymentAmount: setCurrencyPrecision(creditListLine.paymentAmount, currencyPrecision),
                        isEmpty: false,
                        id: uniqueId('netSuiteCreditListLine-'),
                    }))
                );
            }

            if (customFields) {
                setCustomFieldValuesBulk(customFields.length > 0 ? convertCustomFieldsToState(customFields) : {});
            }
        },
        () => {
            resetBillPaymentFormState();
        }
    );

    useProgressLoading(isSaving);

    const { isBelgradeUIFilesUploadSection } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider maxSize={pageContext.attachmentMaxSize} onUpload={onUploadAttachments}>
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarText>{messages.toolbarText}</ToolbarText>

                    <ActionsBlock
                        customGetRequestTransfer={getRequestTransfer}
                        customRequestValidationFn={getValidationErrors}
                        customSaveAndSubmitRequest={saveAndSubmit}
                        isSaving={isSaving}
                    />
                </Toolbar>

                <Content>
                    <ContentInner>
                        <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                        <NetSuiteBillPaymentFieldsSection
                            requestId={request.id}
                            companyId={request.companyId}
                            savedPayeeId={request.details.payee?.id}
                            workflowId={request.template.id}
                        />

                        <NetSuiteBillPaymentBillsSection
                            request={request}
                            payee={fieldValues.payee}
                            currency={currency}
                            accountId={fieldValues.payablesAccount?.id}
                        />

                        {isFieldVisible(attachmentsFieldSettings.attachments) &&
                            (isBelgradeUIFilesUploadSection ? (
                                <Grid gridTemplateColumns='50%' gap={16}>
                                    <AttachmentsSection
                                        request={request}
                                        required={isFieldMandatory(attachmentsFieldSettings.attachments)}
                                    />
                                </Grid>
                            ) : (
                                <FilesSection required={isFieldMandatory(attachmentsFieldSettings.attachments)} />
                            ))}
                    </ContentInner>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

NetSuiteBillPaymentRequestForm.displayName = 'NetSuiteBillPaymentRequestForm';
